import type { PromiseCompletion } from "../classes/PromiseCompletion";
import { toastService } from '../components/Toast.tsx';

export interface AjaxOptions extends RequestInit {
  loader?: PromiseCompletion;
  onRateLimit?: () => void; // Optional callback for rate limit handling
  checkStatusCode?: boolean;
  allowUnauthenticated?: boolean;
  suppressStatuses?: number[];
}

let refreshTokenPromise: Promise<void> | null = null;

class HttpError extends Error {
  status: number;
  payload: string;

  constructor(status: number, payload: string) {
    super(`HTTP ${status}: ${payload}`);
    this.name = 'HttpError';
    this.status = status;
    this.payload = payload;
  }
}

async function readErrorPayload(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();

  if (!text) {
    return response.statusText || 'Empty error response';
  }

  if (contentType.includes('application/json')) {
    try {
      const json = JSON.parse(text);
      if (typeof json === 'string') {
        return json;
      }
      return JSON.stringify(json, null, 2);
    } catch {
      return text;
    }
  }

  return text;
}

class apiService {

  private async _execFetch(url: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', options?: AjaxOptions): Promise<Response> {
    const headers = {
      ...options?.headers,
    };

    const request = fetch(url, {
      method,
      headers,
      body: options?.body
    });

    options?.loader?.subscribe(request);

    const response = await request;

    if (response.status === 401 && !options?.allowUnauthenticated) {

      if (refreshTokenPromise){
        await refreshTokenPromise;
      } else {
        refreshTokenPromise = new Promise(async (resolve, reject) => {
          try {
            const usernameCookie = document.cookie.split('; ').find(row => row.startsWith('username='));
            if (usernameCookie) {
              //try to refresh the token
              const refreshRequest = fetch('/auth/refresh', {
                method: 'POST'
              });
              if (options?.loader) {
                options?.loader.subscribe(refreshRequest);
              }

              const refreshResponse = await refreshRequest;
              if (refreshResponse.ok) {
                // If the refresh was successful, resolve the promise
                resolve();
              } else {
                // If the refresh failed, redirect to login
                if (refreshResponse.status === 401) {
                  reject(new Error('Failed to refresh token. Redirecting to login.'));
                  window.location.href = '/auth/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
                } else {
                  reject(new Error(`Failed to execute request due to token expiration and failed to refresh token. Status: ${refreshResponse.status}, online: ${navigator.onLine}`));
                }
              }
            } else {
              reject(new Error('No username cookie found.'));
            }
          } catch (error) {
            reject(error);
          } finally {
            refreshTokenPromise = null; // Reset the promise after completion
          }
        }
      );
      await refreshTokenPromise;
      return this._execFetch(url, method, options); // Retry the request after refreshing the token
    }
  }

    if (response.status === 429 && options?.onRateLimit) {
      options.onRateLimit();
      throw new Error('Rate limit exceeded. Please try again later.');
    }

    if ((options?.checkStatusCode === undefined || options.checkStatusCode) && !response.ok) {
      const payload = await readErrorPayload(response);
      if (response.status >= 400 && !options?.suppressStatuses?.includes(response.status)) {
        toastService.error(payload, `Request failed (${response.status})`);
      }
      throw new HttpError(response.status, payload);
    }

    return response;
  }

  public async get<T>(url: string, options?: AjaxOptions): Promise<T> {
    const response = await this._execFetch(url, 'GET', options);

    const jsonParseTask = response.json();
    if (options?.loader) {
      options.loader.subscribe(jsonParseTask);
    }

    const result = await jsonParseTask;
    return result as T;
  }

  public async post<T = unknown>(url: string, data?: unknown, options?: AjaxOptions): Promise<T> {
    const response = await this._execFetch(url, 'POST', {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(data),
      loader: options?.loader
    });

    const jsonParseTask = response.json();
    if (options?.loader) {
      options.loader.subscribe(jsonParseTask);
    }

    const result = await jsonParseTask;
    return result as T;
  }
  
  public async put<T = unknown>(url: string, data?: unknown, options?: AjaxOptions): Promise<T> {
    const response = await this._execFetch(url, 'PUT', {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: JSON.stringify(data),
      loader: options?.loader
    });

    const jsonParseTask = response.json();
    if (options?.loader) {
      options.loader.subscribe(jsonParseTask);
    }

    const result = await jsonParseTask;
    return result as T;
  }

  public async delete(url: string, options?: AjaxOptions): Promise<void> {
    await this._execFetch(url, 'DELETE', options);
  }
}

export default new apiService();